import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    key: String,
    session: String,
  }

  static targets = ["address", "city", "region", "country", "postalCode"]

  async initialize() {
    if (this.isGoogleMapsLoaded()) {
      await this.initAutoComplete()
    } else {
      await this.loadGoogleMapsAPI()
      await this.initAutoComplete()
    }
  }

  isGoogleMapsLoaded() {
    return typeof google !== 'undefined' && google.maps && google.maps.importLibrary
  }

  async loadGoogleMapsAPI() {
    const scriptId = "forms--address-autocomplete-script-tag"
    
    if (document.getElementById(scriptId)) return

    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.id = scriptId
      // TODO: change this to quarterly updates after Sep 1, 2025
      // https://developers.google.com/maps/documentation/javascript/versions
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.keyValue}&loading=async&libraries=places&v=weekly`
      script.async = true
      
      script.onload = () => this.waitForGoogleMapsReady(resolve)
      script.onerror = () => reject(new Error('Failed to load Google Maps API'))
      
      document.head.appendChild(script)
    })
  }

  waitForGoogleMapsReady(resolve) {
    const checkAPI = () => {
      if (this.isGoogleMapsLoaded()) {
        resolve()
      } else {
        setTimeout(checkAPI, 100)
      }
    }
    checkAPI()
  }

  // https://developers.google.com/maps/documentation/javascript/place-autocomplete-overview
  async initAutoComplete() {
    try {
      const { PlaceAutocompleteElement } = await google.maps.importLibrary("places")
      
      const input = this.getAddressInput()
      if (!input) return

      const autocomplete = new PlaceAutocompleteElement()
      autocomplete.options = { fields: ["address_components"], types: ["address"] }

      this.replaceInputWithAutocomplete(input, autocomplete)
      this.setupAutocompleteListener(autocomplete)
      
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error)
    }
  }

  getAddressInput() {
    return document.getElementById("address_address")
  }

  replaceInputWithAutocomplete(input, autocomplete) {
    input.parentNode.insertBefore(autocomplete, input)
    input.style.display = 'none'
    this.styleAutocompleteElement(autocomplete)
  }

  setupAutocompleteListener(autocomplete) {
    autocomplete.addEventListener('gmp-select', async (event) => {
      const place = event.placePrediction.toPlace()
      
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress']
      })

      this.parseAndUpdateAddress(place)
    })
  }

  parseAndUpdateAddress(place) {
    const addressData = this.extractAddressComponents(place)
    this.updateAddressFields(addressData)
  }

  extractAddressComponents(place) {
    const components = {
      address: '',
      city: '',
      region: '',
      country: '',
      postal_code: ''
    }

    if (place.addressComponents) {
      place.addressComponents.forEach(component => {
        const types = component.types
        
        if (types.includes('street_number')) {
          components.address = component.shortText + ' '
        }
        if (types.includes('route')) {
          components.address += component.shortText
        }
        if (types.includes('locality')) {
          components.city = component.shortText
        }
        if (types.includes('administrative_area_level_1')) {
          components.region = component.shortText
        }
        if (types.includes('country')) {
          components.country = component.shortText
        }
        if (types.includes('postal_code')) {
          components.postal_code = component.shortText
        }
      })
    }

    if (!components.address && place.formattedAddress) {
      components.address = place.formattedAddress
    }

    return components
  }

  updateAddressFields({ address, city, region, country, postal_code }) {
    $("#address_address").val(address)
    $("#address_city").val(city)
    $("#address_country").val(country).change()
    $("#address_postal_code").val(postal_code)
    $("#address_region").val(region)
  }

  styleAutocompleteElement() {
    const style = document.createElement('style')
    style.textContent = `
      gmp-place-autocomplete {
        color-scheme: light;
      }
    `

    document.head.appendChild(style)
  }
}
